/**
 * Fixed addresses this page reads from and links to. Everything here is
 * public, first-party data — no third-party origin is ever fetched from or
 * loaded into this page (see the module comment in `main.ts`).
 */
import { PUBLIC_KEYS } from '../../../scripts/ipfs-release/public-keys'

/** Where the signed release list lives. Same origin the primary link opens. */
export const MANIFEST_BASE_URL = 'https://ipfs.gibs.finance'

export const MANIFEST_URL = `${MANIFEST_BASE_URL}/version.json`
export const SIGNATURE_URL = `${MANIFEST_BASE_URL}/version.json.sig`

/**
 * The public copy of the signed list on GitHub. The page FETCHES the list from
 * {@link MANIFEST_URL}, but it never shows that Gibs-run address to a reader.
 * Links that a reader sees point to this copy, which Gibs does not host.
 */
export const MANIFEST_MIRROR_URL = 'https://raw.githubusercontent.com/gibsfinance/interface-releases/main/version.json'
export const SIGNATURE_MIRROR_URL = `${MANIFEST_MIRROR_URL}.sig`

/**
 * The app route every "Open app" link names. The app uses hash routing,
 * and its empty route is its own welcome view, so the link names the bridge.
 */
export const APP_ENTRY_ROUTE = '#/bridge'

/**
 * The origin of the production app. It is a separate app, so the page never
 * advertises it. Only the redirect for old `gibs.finance/#/` links reads it
 * (`legacyHashRedirect.ts`).
 */
export const LATEST_APP_ORIGIN = 'https://latest.gibs.finance'

/**
 * The staging app. The Worker serves the staging channel there, so a reader
 * who picks Beta opens it on its own origin.
 */
export const STAGING_APP_URL = `https://next.gibs.finance/${APP_ENTRY_ROUTE}`

/** The landing page itself. It lists every signed release. */
export const LANDING_URL = 'https://gibs.finance/'

export const RELEASES_REPOSITORY_URL = 'https://github.com/gibsfinance/interface-releases'
export const SELF_HOST_DOCS_URL = `${RELEASES_REPOSITORY_URL}/tree/main/self-host`
export const PUBLIC_KEYS_FILE_URL = `${RELEASES_REPOSITORY_URL}/blob/main/public-keys.json`

/**
 * Builds a GitHub Release page URL for one release id. Links to the PUBLIC
 * releases repository, not the private source repository
 * (`gibsfinance/bridge`) — the private repository's commit history is not
 * reachable by a visitor, but every release id is also the GitHub Release
 * tag in the public repository, and that page shows the release's full
 * commit hash.
 */
export const releasePageUrl = (releaseId: string): string =>
  `${RELEASES_REPOSITORY_URL}/releases/tag/${releaseId}`

/**
 * `keyId` -> base64 raw Ed25519 public key, in the shape `verifyRelease`
 * wants. Derived from the same registry `publish.ts` and the redirect
 * Worker read, so this page can never disagree with either about which key
 * is trusted.
 */
export const PUBLIC_KEYS_BY_KEY_ID: Record<string, string> = Object.fromEntries(
  Object.entries(PUBLIC_KEYS).map(([keyId, entry]) => [keyId, entry.publicKeyBase64]),
)

/**
 * Display names for release channels. A channel absent from this map shows
 * its own name. The tooltip on each toggle option still names the branch,
 * so a reader can always see which branch a label stands for.
 */
export const CHANNEL_LABELS: Readonly<Record<string, string>> = Object.freeze({
  master: 'Stable',
  staging: 'Beta',
})

/**
 * Where the downloadable local server listens. It serves the verified
 * release from the reader's own computer, and answers
 * `GET /version.json` with the verified release list plus
 * {@link LOCAL_COPY_SERVED_RELEASE_FIELD}. Its CORS policy admits
 * gibs.finance, next.gibs.finance, and our gateway hosts, so this page can
 * read that answer.
 */
export const LOCAL_COPY_ORIGIN = 'http://127.0.0.1:7369'

/** The link the "Open your local copy" button opens. */
export const LOCAL_COPY_URL = `${LOCAL_COPY_ORIGIN}/`

/** The file this page probes to learn whether a local copy is running. */
export const LOCAL_COPY_VERSION_URL = `${LOCAL_COPY_ORIGIN}/version.json`

/**
 * The field of the local server's `/version.json` that names the release id
 * (`<version>+<7-char commit>`) it serves. The local server must write this
 * exact field name; the probe treats an answer without it as "no local copy".
 */
export const LOCAL_COPY_SERVED_RELEASE_FIELD = 'servedReleaseId'

/**
 * How long the probe waits for the local server before it gives up. The
 * server is on the same machine, so a running one answers in a few
 * milliseconds; this only bounds the wait when nothing listens.
 */
export const LOCAL_COPY_PROBE_TIMEOUT_MILLISECONDS = 1500

/**
 * Release assets for the local server. `.github/workflows/release-local-server.yml`
 * replaces each asset, under exactly these names, on the rolling `local-server`
 * release of the public releases repository. Not `/releases/latest/download`:
 * every interface release takes GitHub's "Latest" marker there, so that path
 * points at an interface release, which carries none of these files.
 */
export const LOCAL_COPY_DOWNLOAD_BASE_URL = `${RELEASES_REPOSITORY_URL}/releases/download/local-server`

/** One downloadable build of the local server. */
export interface LocalCopyDownload {
  /** The platform name a reader recognizes. */
  readonly label: string
  /** The release asset name: `gibs-local-<os>-<arch>[.exe]`. */
  readonly asset: string
}

/** Every platform build the release workflow publishes, in display order. */
export const LOCAL_COPY_DOWNLOADS: readonly LocalCopyDownload[] = Object.freeze([
  { label: 'Windows', asset: 'gibs-local-windows-amd64.exe' },
  { label: 'Windows ARM', asset: 'gibs-local-windows-arm64.exe' },
  { label: 'macOS Apple silicon', asset: 'gibs-local-darwin-arm64' },
  { label: 'macOS Intel', asset: 'gibs-local-darwin-amd64' },
  { label: 'Linux', asset: 'gibs-local-linux-amd64' },
  { label: 'Linux ARM', asset: 'gibs-local-linux-arm64' },
])

/** The sha256 checksums file the release workflow publishes beside the builds. */
export const LOCAL_COPY_CHECKSUMS_ASSET = 'checksums.txt'

/** The download URL for one release asset of the local server. */
export const buildLocalCopyDownloadUrl = (asset: string): string =>
  `${LOCAL_COPY_DOWNLOAD_BASE_URL}/${asset}`

/** One community-run site that links to or embeds Gibs Finance. */
export interface CommunitySite {
  readonly name: string
  readonly url: string
}

/**
 * Community sites shown at the bottom of the page. Empty until the owner
 * names a real one; while empty, the page renders no community section at
 * all. Add only sites that exist — never a placeholder.
 */
export const COMMUNITY_SITES: readonly CommunitySite[] = Object.freeze([])
